import React, { useEffect, useState, useContext, useCallback } from "react";
import {
  Box, Typography, Paper, Switch, Chip, CircularProgress,
  Alert, Snackbar, Tooltip,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import ExtensionIcon from "@mui/icons-material/Extension";
import api from "../../utils/api";
import { ModulesContext } from "../../utils/ModulesContext";
import { PermissionsContext } from "../../utils/PermissionsContext";

/* ── Per-module display metadata ─────────────────────────────────────── */
const MODULE_META = {
  1: {
    icon: <BuildIcon sx={{ fontSize: 22 }} />,
    iconBg: "rgba(47,99,173,0.1)",
    iconColor: "#2f63ad",
    description: "Track maintenance tasks, assign staff, and monitor resolution progress.",
  },
  2: {
    icon: <ErrorOutlineIcon sx={{ fontSize: 22 }} />,
    iconBg: "rgba(211,47,47,0.08)",
    iconColor: "#c62828",
    description: "Issue and manage community violations, track fines and resolutions.",
  },
  3: {
    icon: <AccountBalanceIcon sx={{ fontSize: 22 }} />,
    iconBg: "rgba(46,125,50,0.08)",
    iconColor: "#2e7d32",
    description: "Handle member requests for property modifications and approvals.",
  },
  4: {
    icon: <HelpOutlineIcon sx={{ fontSize: 22 }} />,
    iconBg: "rgba(245,124,0,0.08)",
    iconColor: "#e65100",
    description: "Allow members to submit general support or inquiry requests.",
  },
  5: {
    icon: <DirectionsBoatIcon sx={{ fontSize: 22 }} />,
    iconBg: "rgba(2,136,209,0.08)",
    iconColor: "#0277bd",
    description: "Manage docks, slips, reservations, vessel records, and marina maps.",
  },
};

const defaultMeta = {
  icon: <ExtensionIcon sx={{ fontSize: 22 }} />,
  iconBg: "rgba(97,97,97,0.08)",
  iconColor: "#424242",
  description: "System module.",
};

/* ── Component ───────────────────────────────────────────────────────── */
const ModuleSettings = () => {
  const { refetch: refetchContext } = useContext(ModulesContext);
  const perms = useContext(PermissionsContext);
  const canManage = perms.includes("manage_settings");

  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null); // id of module being toggled
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const showSnack = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  const fetchModules = useCallback(async () => {
    try {
      const res = await api.get("/modules");
      setModules(res.data || []);
    } catch {
      showSnack("Failed to load modules.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const handleToggle = async (mod) => {
    if (!canManage) return;

    // Prevent turning off the last active module
    const activeCount = modules.filter((m) => m.is_active).length;
    if (mod.is_active && activeCount <= 1) {
      showSnack("At least one module must remain active.", "warning");
      return;
    }

    setToggling(mod.id);
    const newActive = mod.is_active ? 0 : 1;
    try {
      await api.put(`/modules/${mod.id}`, { is_active: newActive });
      setModules((prev) =>
        prev.map((m) => (m.id === mod.id ? { ...m, is_active: Boolean(newActive) } : m))
      );
      // Notify the global context so the sidebar updates immediately
      refetchContext();
      showSnack(`${mod.name} ${newActive ? "activated" : "deactivated"}.`);
    } catch {
      showSnack(`Failed to update ${mod.name}.`, "error");
    } finally {
      setToggling(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Module Settings
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Enable or disable system modules. Deactivated modules are hidden from the sidebar
          and all their routes become inaccessible immediately. no restart required.
        </Typography>
      </Box>

      {/* Warning banner */}
      <Alert severity="info" sx={{ mb: 3, borderRadius: "12px" }}>
        Changes take effect <strong>instantly</strong>. Deactivating a module hides it for
        all users regardless of their permissions.
      </Alert>

      {/* Module cards */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {modules.map((mod) => {
          const meta = MODULE_META[mod.id] ?? defaultMeta;
          const isToggling = toggling === mod.id;

          return (
            <Paper
              key={mod.id}
              elevation={0}
              sx={{
                borderRadius: "14px",
                border: mod.is_active
                  ? "1px solid rgba(47,99,173,0.12)"
                  : "1px solid rgba(107,119,140,0.15)",
                backgroundColor: mod.is_active ? "rgba(255,255,255,0.98)" : "rgba(248,250,252,0.7)",
                boxShadow: mod.is_active
                  ? "0 4px 14px rgba(0,0,0,0.04)"
                  : "none",
                transition: "all 0.2s ease",
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              {/* Module icon */}
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: "12px",
                  backgroundColor: mod.is_active ? meta.iconBg : "rgba(107,119,140,0.08)",
                  color: mod.is_active ? meta.iconColor : "#9aa5b4",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.2s ease",
                }}
              >
                {meta.icon}
              </Box>

              {/* Name + description */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.25 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: mod.is_active ? "#223142" : "#9aa5b4",
                      transition: "color 0.2s",
                    }}
                  >
                    {mod.name}
                  </Typography>
                  <Chip
                    label={mod.is_active ? "Active" : "Inactive"}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: mod.is_active
                        ? "rgba(22,163,74,0.1)"
                        : "rgba(107,119,140,0.1)",
                      color: mod.is_active ? "#15803d" : "#6b7280",
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: mod.is_active ? "#6b778c" : "#b0b8c4",
                    fontSize: "0.82rem",
                    transition: "color 0.2s",
                  }}
                >
                  {meta.description}
                </Typography>
              </Box>

              {/* Toggle */}
              {isToggling ? (
                <CircularProgress size={22} sx={{ flexShrink: 0 }} />
              ) : (
                <Tooltip
                  title={
                    !canManage
                      ? "You don't have permission to change this"
                      : mod.is_active
                      ? "Deactivate module"
                      : "Activate module"
                  }
                  placement="left"
                >
                  <span>
                    <Switch
                      checked={Boolean(mod.is_active)}
                      onChange={() => handleToggle(mod)}
                      disabled={!canManage}
                      color="success"
                      sx={{ flexShrink: 0 }}
                    />
                  </span>
                </Tooltip>
              )}
            </Paper>
          );
        })}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snack.sev} onClose={() => setSnack((p) => ({ ...p, open: false }))}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ModuleSettings;
